// Dependencies
import React, { useEffect, useState, useRef, useMemo } from "react";
import Link from "@mui/material/Link";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
// Components
import Modal, { ModalBody, ModalFooter } from "Components/Modal";
import { Box, Grid } from "@mui/material";
import Typography from "@mui/material/Typography";
import Select from "Components/Select";
import Form from "Components/Form";
import { NotificationManager } from "Components/Notifications";
// The react controls data picker is not prepared to exclude non-clip dates, that's why the ui components are used
import { DatePicker } from "Components/uiControls/DatePicker";
// DatePicker to SW
import { DatePicker as RCDatePicker } from "@gpstrackit/gtc-react-controls";
import Calendar from "Components/uiControls/calendar";
import Slider from "Components/Slider";
import { Slider as MuiSlider } from "@mui/material";

import { Pill, Button } from "Components";

// Actions
import {
  clipRequestGetUnits,
  clipRequestOpenForm,
  setRequestClipData,
  searchAvailableClips,
} from "Redux/actions";

import { showNotification } from "Modules/video/.globals/helpers";
import {
  useCreateClipRequest,
  useCreateSmartWitnessClipRequest,
} from "Modules/video/.globals/api/clipRequests";

import { getPreferencesByKey } from "Core/data/Helpers";

// Helpers
import { useIntl } from "react-intl";
// Styles
import "./styles.scss";
import { Skeleton } from "@mui/material";
import { Tooltip } from "Components";
import { RefreshAvClips } from "Modules/.common/components/refreshAvClips";
import RequestVideo from "Modules/.common/components/RequestVideo";

const SMARTWITNESS_MANUFACTURER = "SMARTWITNESS";
const LIGTHMETRICS_MANUFACTURER = "LIGHTMETRICS";
const MITAC_MANUFACTURER = "MITAC";
const VIDFLEET_MANUFACTURER = "VIDFLEET";


/**
 * El listado de unidades no incluye `linkedUnit`; el formulario lo construye para leer fabricante / ESN / TZ del video device.
 * Debe usarse también cuando `requestClipData` repuebla la unidad (p. ej. clic en un clip del calendario), si no `manufacturerName` queda vacío.
 */
const enrichVideoUnitWithLinkedDevice = (unit) => {
  if (unit?.videoDevices?.length) {
    return {
      ...unit,
      linkedUnit: {
        id: unit.videoDevices[0]?.id,
        manufacturerName: unit.videoDevices[0]?.manufacturer,
        timeZone: unit.timeZone,
        esn: unit.videoDevices[0]?.esn,
      },
    };
  }
  // Mitac is a standalone GPS+camera unit — it has no separate video device row,
  // so it never shows up in unit.videoDevices; detect it from unit.manufacturerName instead.
  if (unit?.manufacturerName?.toUpperCase() === MITAC_MANUFACTURER) {
    return {
      ...unit,
      linkedUnit: {
        id: unit.id,
        manufacturerName: "Mitac",
        timeZone: unit.timeZone,
        esn: unit.esn,
      },
    };
  }
  return unit;
};

/**
 * Renders a side menu to create a VideoClip.
 * @param {Object} props - Component props.
 * @returns {React.Node} -The side menu component.
 */
const ClipRequestForm = (props) => {
  // Props
  const { newClipRequestOpen, unitList } = props;

  const { messages, locale } = useIntl();

  const initialState = {
    deviceId: "",
    dateRange: { start: "", end: "" },
    gpsId: "",
  };
  // Hooks
  const dispatch = useDispatch();
  const clipRequestForm = useRef();
  // Local state
  const [currentClip, setCurrentClip] = useState(initialState);
  const [selectedTime, setSelectedTime] = useState();
  const [formSW, setformSW] = useState({
    date: new Date(new Date().setSeconds(0, 0)),
    durationAfter: 10,
    durationBefore: 10,
    mediaType: "image",
  });
  const [selectedDateLM, setSelectedDateLM] = useState(moment().startOf("day"));
  const [selectedTimeLM, setSelectedTimeLM] = useState(moment().format("HH:mm"));
  const [durationLM, setDurationLM] = useState(60);
  const [seconds, setSeconds] = useState(10);
  const [, setLapseLabel] = useState("");
  const [availableTimeClips, setAvailableTimeClips] = useState([]);
  const [activateSearch, setActivateSearch] = useState(false);
  const [loadingAvClips, setLoadingAvClips] = useState(false);
  const [unitListSelected, setUnitListSelected] = useState();
  const [preferences, setPreferences] = useState({});
  const [clipsPerDay, setClipsPerDay] = useState([]);

  const { currentTab } = useSelector((state) => state.videoMainRedux);

  const { reponsehDeviceReadingVideo } = useSelector((state) => state.clipRequestRedux);
  const {
    units,
    newClipRequest,
    loadingUnits,
    availableClips,
    requestClipData,
  } = useSelector((state) => state.clipsRedux);
  const preferencesUser = useSelector(
    (state) => state.preferencesRedux.preferencesUser
  );
  const keyPreferences = "mapSettings";

  const manufacturerName = unitListSelected?.linkedUnit?.manufacturerName;
  // LightMetrics units are requested with the standalone RequestVideo component instead of this form.
  const isLightMetricsUnit = manufacturerName?.toUpperCase() === LIGTHMETRICS_MANUFACTURER;

  // searchVideoDevices (source of `units`) only returns units with a linkedDevice row,
  // so standalone Mitac units never appear there — merge them in from the realtime
  // catalog (`unitList`) until the backend query is updated to include them.
  const unitOptions = useMemo(() => {
    const mitacStandaloneUnits = (unitList || [])
      .filter((unit) => !unit?.videoDevices?.length && unit?.manufacturerName?.toUpperCase() === MITAC_MANUFACTURER)
      .map((unit) => ({ label: unit.label, id: unit.id, deviceModelName: "Mitac" }));
    return [...(units || []), ...mitacStandaloneUnits];
  }, [units, unitList]);

  // Component calculations
  // const selectedUnit = units?.find((unit) => unit.id === currentClip.deviceId);

  const calculateRange = (selectedDate, lapse = 10) => {
    const currentDate = new Date(selectedDate);
    const startDate = moment(currentDate).subtract(lapse, "s").toDate();
    const finalDate = moment(currentDate).add(lapse, "s").toDate();
    setLapseLabel(buildClipLapseLabel(startDate, finalDate));

    return { start: startDate, end: finalDate };
  };

  /**
   * Handles the getUnits side effect to populate the form units.
   */
  useEffect(() => {
    if (currentTab === 1) {
      dispatch(clipRequestGetUnits());
    }
    clearFields();
  }, [currentTab]);

  /**
   * Handles the date range for the video clip.
   */
  useEffect(() => {
    const dateRange = calculateRange(selectedTime, seconds);
    setCurrentClip((prevState) => ({ ...prevState, dateRange }));
  }, [selectedTime, seconds, locale]);

  useEffect(() => {
    if (preferencesUser) {
      let newPreferences = getPreferencesByKey(preferencesUser, keyPreferences);
      setPreferences(newPreferences);
    }
  }, [preferencesUser]);

  const buildClipLapseLabel = (startDate, endDate) => {
    const start = moment(startDate);
    const end = moment(endDate);
    const lapse = `${messages["videoClipRequest_from"]} ${start.format(
      "LTS"
    )} ${messages["videoClipRequest_to"]} ${end.format("LTS")}`;

    return lapse;
  };

  const getFormatedRange = (dateRange) => {
    const { start: startDate, end: endDate } = dateRange;
    const formatedRange = {
      start: moment(startDate).format(),
      end: moment(endDate).format(),
    };
    return formatedRange;
  };

  const handleOnValidSubmit = () => {
    const formattedDate = getFormatedRange(currentClip.dateRange);
    const dataToSave = {
      deviceId: currentClip.deviceId,
      dateRange: formattedDate,
    };
    createClip(dataToSave);
  };

  useEffect(() => {
    if (availableClips && availableClips?.length) {
      getClipsPerDay();
    }
  }, [selectedTime]);

  //Timezone mode
  useEffect(() => {
    if (availableClips && availableClips?.length) {
      const avClips = availableClips[0]?.validTimeRangeSeq;
      const clipsLastDay = [];
      if (!avClips) return;
      const lastClip = avClips[avClips.length - 1];
      const parseLastClip = JSON.parse(lastClip)[0];
      const timezone = unitListSelected?.timeZone?.name;
      const formatAvailableClips = avClips.map((item) => {
        const itemDate = JSON.parse(item);
        const getDataCurrentDay = moment(parseLastClip).isSame(
          moment(itemDate[0]),
          "day"
        );
        if (getDataCurrentDay) {
          clipsLastDay.push(itemDate[0]);
        }
        const date = new Date(itemDate[0]).toLocaleString("en", {
          timeZone: timezone,
        });
        const parseDate = new Date(date);
        return parseDate;
      });
      const restMinute = moment(clipsLastDay[0]).subtract(2, "seconds");
      setSelectedTime(new Date(restMinute));
      setLoadingAvClips(false);
      setAvailableTimeClips(formatAvailableClips);
    } else {
      setAvailableTimeClips([]);
      setLoadingAvClips(false);
    }
  }, [availableClips]);

  useEffect(() => {
    if (!requestClipData?.deviceId) return;
    const rawUnit = unitList.find((item) =>
      item.videoDevices?.find(
        (device) => device.id === Number(requestClipData?.deviceId)
      )
    );
    if (!rawUnit) return;
    const selectedUnit = enrichVideoUnitWithLinkedDevice(rawUnit);
    setUnitListSelected(selectedUnit);
    setCurrentClip((prevState) => ({
      ...prevState,
      deviceId: selectedUnit?.videoDevices[0]?.id
        ? selectedUnit.videoDevices[0].id
        : selectedUnit?.linkedUnit?.id,
      label: selectedUnit?.label,
      gpsId: selectedUnit?.videoDevices[0]?.id
        ? selectedUnit.videoDevices[0].id
        : selectedUnit?.linkedUnit?.id,
    }));
  }, [requestClipData?.deviceId]);

  const clearFields = () => {
    setSelectedTime(new Date());
    setAvailableTimeClips([]);
    dispatch(setRequestClipData(null));
    setUnitListSelected();
    setCurrentClip(initialState);
    setActivateSearch(false);
  };

  const resetAfterCreate = () => {
    setSelectedTime(new Date());
    setSeconds(10);
    setLapseLabel("");
    setUnitListSelected();
    setCurrentClip(initialState);
  };

  // El panel también se cierra desde fuera (SliderRangePanel): al cerrarse, limpia el estado local.
  useEffect(() => {
    if (!newClipRequest) resetAfterCreate();
  }, [newClipRequest]);

  const { mutate: createClip, isPending: creatingClip } = useCreateClipRequest({
    onSuccess: () => {
      resetAfterCreate();
      dispatch(newClipRequestOpen(false));
      showNotification("", messages["videoClipRequest_successCreation"], "success");
    },
    onError: (error) => {
      NotificationManager.error(messages[error?.messageKey], messages["error"], 3000, null, null, "warning");
      NotificationManager.warning(
        messages[manufacturerName?.toUpperCase() === LIGTHMETRICS_MANUFACTURER ? "videoMainView_errorRequestClipLM" : "videoMainView_errorRequestClip"],
        messages["videoClipRequest_Alert"],
        4000,
        null,
        null,
        "info",
        null
      );
    },
  });

  const { mutate: createClipSW, isPending: creatingClipSW } = useCreateSmartWitnessClipRequest({
    onSuccess: () => {
      clearFields();
      dispatch(newClipRequestOpen(false));
      showNotification("", messages["videoClipRequest_successCreation"], "success");
    },
    onError: (error) => {
      NotificationManager.warning(
        error?.errorInfo?.Message,
        messages["videoClipRequest_failed"],
        3000,
        null,
        null,
        "info"
      );
    },
  });

  const getClipsPerDay = () => {
    if (availableClips && availableClips?.length) {
      const avClips = availableClips[0]?.validTimeRangeSeq;
      if (!avClips || avClips?.length === 0) return setClipsPerDay([])
      const timezone = unitListSelected?.linkedUnit?.timeZone?.name;
      const newRanges = [];
      const formatAvailableClips = avClips.map((item) => {
        const itemDate = JSON.parse(item);
        const getDestructureClipsPerHour = destructureClipsPerHour(itemDate);
        const tooltipStart = new Date(
          new Date(itemDate[0]).toLocaleString("en", { timeZone: timezone })
        );
        const tooltipEnd = new Date(
          new Date(itemDate[1]).toLocaleString("en", { timeZone: timezone })
        );
        const getRanges = getDestructureClipsPerHour.map((range) => {
          let startDate = new Date(
            new Date(range[0]).toLocaleString("en", { timeZone: timezone })
          );
          let endDate = new Date(
            new Date(range[1]).toLocaleString("en", { timeZone: timezone })
          );
          if (moment(moment(endDate)).isAfter(moment(startDate).endOf("day"))) {
            endDate = new Date(
              moment(startDate).endOf("day").subtract(1, "second")
            );
          }
          newRanges.push({
            //fake our per position
            start: new Date(moment(startDate).startOf("hour")),
            end: endDate,
            //real date
            startDate: startDate,
            initTime: new Date(
              new Date(itemDate[0]).toLocaleString("en", { timeZone: timezone })
            ),
            tooltip: `${moment(tooltipStart).format("h:mm a")} - ${moment(
              tooltipEnd
            ).format("h:mm a")}`,
          });
        });

        return newRanges;
      });
      setClipsPerDay(formatAvailableClips[0]);
    } else {
      setClipsPerDay([]);
    }
  };

  const destructureClipsPerHour = (rangeClip) => {
    const [start, end] = rangeClip;

    const intervals = [];
    let startHour = moment(start).startOf("hour");
    const endHour = moment(start).endOf("hour");

    if (endHour.valueOf() > end) return [rangeClip];

    while (startHour < end) {
      const nextHour = moment(startHour).add(1, "hour").startOf("hour");

      if (nextHour > end) {
        intervals.push([startHour.valueOf(), end]);
      } else {
        if (start > startHour.valueOf()) {
          intervals.push([start, nextHour.valueOf()]);
        } else {
          intervals.push([startHour.valueOf(), nextHour.valueOf()]);
        }
      }

      startHour = nextHour;
    }

    return intervals;
  };

  const filterComponents = {
    event: (props) => {
      const ev = props?.event;
      const totalDuration = 3600;
      const diffMinutes = moment(ev?.end).diff(
        moment(ev?.startDate),
        "seconds"
      );
      const timeRatio = diffMinutes / totalDuration;
      const percentageWidth = timeRatio * 100;
      const currentT = moment(ev?.startDate).startOf("hour");
      const secondsDiff = moment(ev?.startDate).diff(currentT, "seconds");
      const propTime = secondsDiff / totalDuration;
      const percetPosition = propTime * 100;

      return (
        <Tooltip placement="top" title={ev?.tooltip}>
          <div
            onClick={() => {
              if (currentClip.deviceId && !requestClipData?.noSearchAvClips) {
                const dateRange = getFormatedRange(currentClip.dateRange);

                let dateTime = moment(ev?.initTime).seconds(0);

                dateTime = new Date(dateTime);
                setSelectedTime(dateTime);

                const unitLabel = currentClip.label;
                const deviceId = currentClip?.gpsId;
                const gpsId = currentClip?.gpsId;

                const dataTimeLine = {
                  ...currentClip,
                  deviceId: deviceId,
                  label: unitLabel,
                  dateRange,
                  dateTime,
                  gpsId,
                  hideNotification: true,
                };
                dispatch(setRequestClipData(dataTimeLine));
              }
              dispatch(clipRequestOpenForm(true));
            }}
            style={{
              zIndex: 1,
              position: "absolute",
              width: `${percentageWidth}%`,
              height: `100%`,
              left: `${percetPosition}%`,
              background: "#3174ad",
              borderRadius: "5px",
            }}
          />
        </Tooltip>
      );
    },
  };

  const [hoursLM, minutesLM] = selectedTimeLM.split(":").map(Number);
  const anchorLM = moment(selectedDateLM).startOf("day").set({ hours: hoursLM, minutes: minutesLM });
  const isTodayLM = moment(selectedDateLM).isSame(moment(), "day");

  const onSubmitLM = () => {
    const dataRange = {
      start: moment(anchorLM).subtract(durationLM, "seconds").toDate(),
      end: moment(anchorLM).add(durationLM, "seconds").toDate(),
    }

    createClip({
      dateRange: dataRange,
      deviceId: unitListSelected?.linkedUnit?.id,
      type: manufacturerName?.toUpperCase() === MITAC_MANUFACTURER ? "mitac" : undefined
    });
  };

  const onSubmitSW = () => {
    let dateUTC = formSW.date;
    if (unitListSelected.timeZone?.name) {
      dateUTC = new Date(
        moment
          .tz(
            formSW.date.toLocaleString("en"),
            "MM/DD/YYYY, hh:mm:ss A",
            unitListSelected.timeZone?.name
          )
          .utc()
      );
    }

    createClipSW({
      ...formSW,
      date: moment(dateUTC).toISOString(),
      deviceId: unitListSelected?.linkedUnit?.id?.toString(),
      esn: unitListSelected?.linkedUnit?.esn,
    });
  };

  const handleCloseForm = () => {
    dispatch(newClipRequestOpen(false));
    clearFields();
  };

  const handleRequestVideoSubmitted = () => {
    dispatch(searchClipRequests({ ...allFilters }));
    handleCloseForm();
  };

  const onSubmit = () => {
    if (manufacturerName?.toUpperCase() === SMARTWITNESS_MANUFACTURER) {
      onSubmitSW();
    } else if (manufacturerName?.toUpperCase() === LIGTHMETRICS_MANUFACTURER || manufacturerName?.toUpperCase() === MITAC_MANUFACTURER) {
      onSubmitLM();
    }
  };

  return (
    <Modal
      open={newClipRequest}
      size="small"
      position="right"
      loading={creatingClip || creatingClipSW}
      className="clip-request-filters_modal"
    >
      <ModalBody className="clip-request-modal-body">
        <Grid>
          <Typography variant="h5">
            {messages["videoClipRequest_new"]}
          </Typography>
          <Link
            className="clear-all-link"
            underline="always"
            onClick={() => clearFields()}
          >
            {messages["clearAll"]}
          </Link>
          <Form ref={clipRequestForm} onValidSubmit={handleOnValidSubmit}>
            <Select
              required
              value={unitListSelected || ""}
              onChange={(clip) => {
                setLoadingAvClips(true);
                setActivateSearch(true);
                let unitSelected = unitList.find((item) => {
                  if (item.videoDevices?.length) {
                    if (item.videoDevices[0]?.id === Number(clip?.id))
                      return item;
                  } else if (item?.manufacturerName?.toUpperCase() === MITAC_MANUFACTURER && String(item.id) === String(clip?.id)) {
                    return item;
                  }
                  return null
                });

                if (unitSelected) {
                  unitSelected = enrichVideoUnitWithLinkedDevice(unitSelected);
                }

                // Only the manufacturers that pick from available clips need that search; SmartWitness and
                // LightMetrics (RequestVideo) have no available-clips flow.
                const selectedManufacturer = unitSelected?.linkedUnit?.manufacturerName?.toUpperCase();
                if (
                  selectedManufacturer === SMARTWITNESS_MANUFACTURER ||
                  selectedManufacturer === LIGTHMETRICS_MANUFACTURER
                ) {
                  setLoadingAvClips(false);
                } else {
                  dispatch(searchAvailableClips({ esn: clip?.id }));
                }

                if (!unitSelected) {
                  setUnitListSelected({ id: 0, label: clip?.label });
                } else {
                  setUnitListSelected(unitSelected);
                }

                setCurrentClip({
                  ...currentClip,
                  ...unitSelected,
                  deviceId: unitSelected?.id ? unitSelected?.id : 0,
                  label: unitSelected?.label,
                  gpsId: clip?.id,
                  deviceModel: clip?.deviceModelName,
                  ...clip,
                });
              }}
              id="clipRequestUnitsSelector"
              placeholder={messages["select"]}
              options={unitOptions}
              textField="label"
              valueField="id"
              label={messages["videoClipRequest_unit"]}
              requiredMessage={messages["required_field"]}
            />
            {currentClip.deviceId &&
              availableTimeClips.length !== 0 &&
              !loadingAvClips &&
              manufacturerName?.toUpperCase() === VIDFLEET_MANUFACTURER && (
                <Box
                  style={{
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Grid
                    style={{
                      width: "100%",
                      marginTop: "10px",
                      position: "relative",
                    }}
                    className="form-item"
                  >
                    <label className="input-normal-label">
                      {messages["infoBubble_Date"]}
                    </label>
                    <DatePicker
                      className="datepicker_container--slider"
                      value={selectedTime}
                      maxDate={new Date()}
                      onChange={(newDate) => {
                        setSelectedTime(newDate);
                      }}
                      disabledDates={
                        availableTimeClips.length > 0 ? availableTimeClips : []
                      }
                      disabled={availableTimeClips.length > 0 ? false : true}
                      inline={availableTimeClips.length > 0}
                    />
                  </Grid>
                  <RefreshAvClips
                    timezone={unitListSelected?.timeZone?.name}
                    id={
                      unitListSelected?.linkedUnit?.id ?? unitListSelected?.id
                    }
                  />
                  <Box mt="10px">
                    {messages["videoClipRequest_message_grid"]}
                  </Box>
                  <div
                    style={{ height: "600px", padding: "15px 3px 10px 0px" }}
                  >
                    <Calendar
                      clipsPerDay={clipsPerDay}
                      selectedTime={new Date(selectedTime)}
                      filterComponents={filterComponents}
                      min={new Date("2023-12-01 00:00:00")}
                      max={new Date("2023-12-02 23:59:00")}
                    />
                  </div>
                </Box>
              )}

            {
              activateSearch &&
              !reponsehDeviceReadingVideo?.items?.length &&
              manufacturerName?.toUpperCase() === VIDFLEET_MANUFACTURER &&
              availableTimeClips.length <= 0 &&
              !loadingAvClips && (
                <Box mt="25px" color="red">
                  {messages["videoClipRequest_noClips"]}
                </Box>
              )}
            {
              loadingAvClips && manufacturerName?.toUpperCase() === VIDFLEET_MANUFACTURER && (
                <>
                  <Box
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginTop: "15px",
                    }}
                  >
                    <Skeleton variant="rect" width="100%" height="50px" />
                  </Box>
                  <Box
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginTop: "15px",
                      padding: "15px 3px 10px 0px",
                    }}
                  >
                    <Skeleton variant="rect" width="100%" height="500px" />
                  </Box>
                </>
              )}
          </Form>
          {
            manufacturerName?.toUpperCase() === SMARTWITNESS_MANUFACTURER && (
              <>
                <Box
                  style={{
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Grid
                    style={{ width: "100%", marginTop: 6, position: "relative" }}
                    className="form-item"
                  >
                    <label className="input-normal-label">
                      {messages["videoClipRequest_date"]}
                    </label>
                    <RCDatePicker
                      value={formSW?.date}
                      maxDate={new Date()}
                      minDate={
                        new Date(new Date().setMonth(new Date().getMonth() - 6))
                      }
                      calendarPosition="bottom"
                      onChange={(newDate) => {
                        setformSW({ ...formSW, date: newDate });
                      }}
                      showButtons
                      showTimePicker
                    />
                    <Typography variant="caption" style={{ color: "gray", marginTop: 4 }}>
                      {messages["videoClipRequest_applyDateTimeHint"]}
                    </Typography>
                  </Grid>
                  <Grid
                    style={{ width: "100%", marginTop: 6, position: "relative" }}
                    className="form-item"
                  >
                    <label className="input-normal-label">
                      {messages["clipRequestAdvancedMediaType"]}
                    </label>
                    <div>
                      <Pill
                        item={{
                          id: "image",
                          name: messages["videoClipRequest_Image"],
                        }}
                        style={{ width: "30%" }}
                        outlined={formSW?.mediaType !== "image"}
                        colorField="default"
                        onClick={(entity) => {
                          setformSW({ ...formSW, mediaType: "image" });
                        }}
                      />
                      <Pill
                        item={{
                          id: "video",
                          name: messages["videoClipRequest_Video"],
                        }}
                        style={{ width: "30%" }}
                        outlined={formSW?.mediaType !== "video"}
                        colorField="default"
                        onClick={(entity) => {
                          setformSW({ ...formSW, mediaType: "video" });
                        }}
                      />
                    </div>
                  </Grid>
                  {formSW?.mediaType == "video" && (
                    <Grid
                      style={{
                        width: "100%",
                        marginTop: 6,
                        position: "relative",
                      }}
                      className="form-item"
                    >
                      <Grid
                        container
                        item
                        direction={"row"}
                        style={{ marginLeft: 10 }}
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Grid item sm={5}>
                          {messages["videoClipRequest_durationBefore"]}:
                        </Grid>
                        <Grid item sm={2}></Grid>
                        <Grid item sm={5}>
                          {messages["videoClipRequest_durationAfter"]}:
                        </Grid>
                        <Grid item sm={5}>
                          <Slider
                            min={0}
                            max={60}
                            sliderOneColor={false}
                            showValue
                            valueLabelDisplay={"off"}
                            value={formSW?.durationBefore}
                            style={{ marginLeft: 6 }}
                            onChange={(event) => {
                              setformSW({
                                ...formSW,
                                durationBefore: event,
                                durationAfter: 60 - event,
                              });
                            }}
                          />
                        </Grid>
                        <Grid item sm={2}></Grid>
                        <Grid item sm={5} style={{ textAlign: "center" }}>
                          <Slider
                            min={0}
                            max={60}
                            sliderOneColor={false}
                            showValue
                            valueLabelDisplay={"off"}
                            value={formSW?.durationAfter}
                            style={{ marginLeft: 6 }}
                            onChange={(event) => {
                              setformSW({
                                ...formSW,
                                durationAfter: event,
                                durationBefore: 60 - event,
                              });
                            }}
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                  )}
                </Box>
              </>
            )}
          {isLightMetricsUnit && (
            <div className="clip-request-lm-block">
              <RequestVideo
                key={unitListSelected?.linkedUnit?.id}
                initialCameraDeviceId={String(unitListSelected?.linkedUnit?.id)}
                hideUnitSelector
                onSubmitted={handleRequestVideoSubmitted}
                onCancel={handleCloseForm}
              />
            </div>
          )}
          {
            manufacturerName?.toUpperCase() === MITAC_MANUFACTURER && (
              <Box
                style={{
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Grid
                  style={{ width: "100%", marginTop: 6, position: "relative" }}
                  className="form-item"
                >
                  <label className="input-normal-label">
                    {messages["videoClipRequest_date"]}
                  </label>
                  <RCDatePicker
                    value={selectedDateLM}
                    maxDate={moment()}
                    minDate={moment().subtract(6, "months")}
                    calendarPosition="bottom"
                    onChange={(newDate) => {
                      setSelectedDateLM(moment(newDate).startOf("day"));
                    }}
                  />
                </Grid>
                <Grid
                  style={{ width: "100%", marginTop: 16, position: "relative" }}
                  className="form-item"
                >
                  <label className="input-normal-label">
                    {messages["videoClipRequest_time"]}
                  </label>
                  <input
                    type="time"
                    value={selectedTimeLM}
                    max={isTodayLM ? moment().format("HH:mm") : undefined}
                    onChange={(e) => setSelectedTimeLM(e.target.value)}
                    style={{
                      padding: "8px",
                      fontSize: 14,
                      borderRadius: 4,
                      border: "1px solid #ccc",
                      width: "150px",
                    }}
                  />
                </Grid>
                <Grid
                  style={{ width: "100%", marginTop: 16, position: "relative" }}
                  className="form-item"
                >
                  <label className="input-normal-label">
                    {messages["videoClipRequest_selectRangeDate"]}
                  </label>
                  <Box px={2}>
                    <MuiSlider
                      max={90}
                      min={0}
                      value={durationLM}
                      getAriaValueText={() => {
                        return `${moment(anchorLM)
                          .add(durationLM, "seconds")
                          .format("hh:mm:ss a")}`;
                      }}
                      step={1}
                      onChange={(_, newValue) => {
                        setDurationLM(newValue < 60 ? 60 : newValue);
                      }}
                    />
                    <Box
                      sx={{ display: "flex", justifyContent: "space-between" }}
                    >
                      <Typography>
                        {moment(anchorLM).subtract(durationLM, "seconds").format("hh:mm:ss a")}
                      </Typography>
                      <Typography>
                        {moment(anchorLM).add(durationLM, "seconds").format("hh:mm:ss a")}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              </Box>
            )}
        </Grid>
      </ModalBody>
      {!isLightMetricsUnit && (
      <ModalFooter>
        <Box style={{ width: "95%", display: "flex", justifyContent: "start" }}>
          <Button
            className="btn-gray"
            onClick={handleCloseForm}
            disabled={loadingUnits}
          >
            {messages["cancel"]}
          </Button>
          {
            manufacturerName?.toUpperCase() !== VIDFLEET_MANUFACTURER && manufacturerName?.toUpperCase() !== undefined && (
              <Button
                className="btn-blue"
                onClick={(e) => {
                  e.preventDefault();
                  onSubmit();
                }}
              >
                {messages["accountContacts_add"]}
              </Button>
            )
          }
        </Box>
      </ModalFooter>
      )}
    </Modal>
  );
};

export default ClipRequestForm;
